import { useState, useEffect } from 'react';
import { tokenAdminService, AdminTokenPackage } from '../../services/token-admin.service';
import { PackageFormModal } from '../../components/admin/PackageFormModal';

export function TokenPackagesPage() {
  const [packages, setPackages] = useState<AdminTokenPackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<AdminTokenPackage | null>(null);

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await tokenAdminService.getPackages();
      setPackages(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar paquetes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Seguro que quieres eliminar este paquete?')) return;
    try {
      await tokenAdminService.deletePackage(id);
      await loadPackages();
    } catch (err: any) {
      setError(err.message || 'Error al eliminar paquete');
    }
  };

  const handleToggleActive = async (pkg: AdminTokenPackage) => {
    try {
      await tokenAdminService.updatePackage(pkg.id, { isActive: !pkg.isActive });
      await loadPackages();
    } catch (err: any) {
      setError(err.message || 'Error al cambiar estado');
    }
  };

  const handleSave = async (data: Partial<AdminTokenPackage>) => {
    try {
      if (editingPackage) {
        await tokenAdminService.updatePackage(editingPackage.id, data);
      } else {
        await tokenAdminService.createPackage(data as any);
      }
      setShowModal(false);
      setEditingPackage(null);
      await loadPackages();
    } catch (err: any) {
      setError(err.message || 'Error al guardar paquete');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando paquetes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">Paquetes de Tokens</h1>
          <p className="text-gray-600">Gestiona los paquetes de tokens disponibles para compra</p>
        </div>
        <div className="flex gap-3">
          <button onClick={loadPackages} className="btn btn-secondary">🔄 Actualizar</button>
          <button onClick={() => { setEditingPackage(null); setShowModal(true); }} className="btn btn-primary">
            + Nuevo Paquete
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {packages.map((pkg) => (
          <div key={pkg.id} className={`bg-white rounded-lg shadow-sm p-6 ${pkg.popular ? 'border-2 border-primary' : ''}`}>
            {pkg.popular && (
              <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                POPULAR
              </span>
            )}
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold">{pkg.name}</h3>
              <p className="text-sm text-gray-500">{pkg.description}</p>
            </div>
            <div className="text-center mb-4">
              <p className="text-4xl font-bold text-primary">{pkg.tokens}</p>
              <p className="text-sm text-gray-500">tokens</p>
            </div>
            <div className="text-center mb-4">
              <p className="text-2xl font-bold">${pkg.price} MXN</p>
              {pkg.savings > 0 && (
                <p className="text-sm text-green-600">Ahorras {pkg.savings}%</p>
              )}
            </div>
            <div className="flex justify-center mb-4">
              <span className={`text-xs px-3 py-1 rounded-full ${
                pkg.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {pkg.isActive ? 'Activo' : 'Inactivo'}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setEditingPackage(pkg); setShowModal(true); }}
                className="flex-1 btn-action"
              >
                ✏️ Editar
              </button>
              <button
                onClick={() => handleToggleActive(pkg)}
                className={`flex-1 btn-action ${pkg.isActive ? 'text-yellow-600' : 'text-green-600'}`}
              >
                {pkg.isActive ? '⏸️ Desactivar' : '▶️ Activar'}
              </button>
              <button
                onClick={() => handleDelete(pkg.id)}
                className="flex-1 btn-action text-red-600"
              >
                🗑️ Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <PackageFormModal
          package={editingPackage}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingPackage(null); }}
        />
      )}
    </div>
  );
}